import {
  SortRounded as SortIcon,
  Check as SelectedIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Divider,
  Fade,
  IconButton as MuiIconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { hasItems } from "common/arrays";
import * as dates from "common/dates";
import * as numbers from "common/numbers";
import { toSentenceCase } from "common/strings";
import Component from "components/Component";
import UserDetail from "components/admin/UserDetail";
import DefaultLoader from "components/common/DefaultLoader";
import InlineSearch from "components/common/InlineSearch";
import StatusBadge from "components/common/StatusBadgeV2";
import Dashboard from "components/layout/Dashboard";
import DashboardContent from "components/layout/DashboardContent";
import { useState } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import * as bffService from "services/bffService";
import * as claimsService from "services/claimsService";
import * as userSelectors from "store/selectors/user/userSelectors";

const BlankSlateRow = ({ message, color, totalCols }) => (
  <TableRow>
    <TableCell colSpan={totalCols} align={"center"}>
      <Typography
        sx={{
          maxWidth: "32ch",
          margin: "6rem auto",
          color: color ? `${color} !important` : null,
        }}
      >
        {message}
      </Typography>
    </TableCell>
  </TableRow>
);

const LoadingRow = ({ totalCols }) => (
  <TableRow>
    <TableCell colSpan={totalCols} align={"center"} sx={{ padding: "5rem 0" }}>
      <DefaultLoader size={48} />
    </TableCell>
  </TableRow>
);

const TableHeadings = ({ includeUser }) => (
  <TableHead>
    <TableRow>
      {includeUser && <TableCell>{"Owner"}</TableCell>}
      <TableCell>{"Created"}</TableCell>
      <TableCell>{"Insured"}</TableCell>
      <TableCell>{"Service"}</TableCell>
      <TableCell align={"center"}>{"Status"}</TableCell>
      <TableCell align={"center"}>{"Inception"}</TableCell>
      <TableCell align={"right"}>{"Price"}</TableCell>
      <TableCell align={"right"}>{"Limit"}</TableCell>
      <TableCell align={"right"}>{"Agg. Limit"}</TableCell>
      <TableCell align={"right"}>{"Deductible"}</TableCell>
      <TableCell />
    </TableRow>
  </TableHead>
);

const TableContent = ({
  data,
  includeUser,
  searchText,
  loading,
  showBlankSlate,
  showNoMatchesFound,
}) => {
  const {
    data: industryClasses,
    isLoading: industryClassesLoading,
  } = claimsService.useIndustryClassesQuery(null);
  const getIndustryClassName = (industryClass) => {
    if (industryClassesLoading) {
      return null;
    }
    const name = (industryClasses ?? []).find((x) => x.key === industryClass)
      ?.name;
    return name ?? toSentenceCase(industryClass);
  };

  const navigate = useNavigate();

  const priceFormatter = numbers.dpString(2, { prefix: "$", nullValue: "-" });
  const numberFormatter = numbers.dpString(0, { prefix: "$", nullValue: "?" });
  const totalCols = includeUser ? 10 : 9;

  const handleItemClicked = (form) => {
    navigate(`/forms/${form.id}${form.isSubmitted ? "" : "/edit"}`);
  };

  return (
    <TableBody>
      {(data ?? []).map((form) => {
        const layers = hasItems(form.formData.layers)
          ? form.formData.layers
          : [
              {
                limit: form.formData.limit,
                aggregate: form.formData.aggregate,
                deductible: form.formData.deductible,
              },
            ];

        return layers.map((layer, index) => (
          <TableRow
            key={`${form.id}-${index}`}
            onClick={() => handleItemClicked(form)}
            sx={{ cursor: "pointer" }}
            hover
            className={"fade-in-slow"}
          >
            {index === 0 && (
              <>
                {includeUser && (
                  <TableCell
                    rowSpan={layers.length}
                    sx={{ verticalAlign: "top" }}
                  >
                    <UserDetail email={form.createdByUserEmail} compact />
                  </TableCell>
                )}
                <TableCell
                  rowSpan={layers.length}
                  sx={{ verticalAlign: "top" }}
                >
                  {dates.readableDate(form.createTime, { includeTime: true })}
                </TableCell>
                <TableCell
                  rowSpan={layers.length}
                  sx={{ verticalAlign: "top" }}
                >
                  {form.formData.applicant}
                </TableCell>
                <TableCell
                  rowSpan={layers.length}
                  sx={{ verticalAlign: "top" }}
                >
                  {getIndustryClassName(form.formData.service)}
                </TableCell>
                <TableCell
                  rowSpan={layers.length}
                  sx={{ verticalAlign: "top" }}
                  align={"center"}
                >
                  {<StatusBadge variant={"chip"} status={form.status} />}
                </TableCell>
                <TableCell
                  rowSpan={layers.length}
                  sx={{ verticalAlign: "top" }}
                  align={"center"}
                >
                  {form.formData.inceptionTbd
                    ? "TBD"
                    : form.formData.inception == null
                    ? "-"
                    : dates.formatDate(form.formData.inception)}
                </TableCell>
              </>
            )}
            <TableCell align={"right"}>
              {priceFormatter(
                form.quote?.premium?.[layer.id] ?? form.quote?.premium ?? "-"
              )}
            </TableCell>
            <TableCell align={"right"}>
              {numberFormatter(layer.limit)}
            </TableCell>
            <TableCell align={"right"}>
              {numberFormatter(layer.aggregate)}
            </TableCell>
            <TableCell align={"right"}>
              {numberFormatter(layer.deductible)}
            </TableCell>
            <TableCell>
              {layer.id === form?.boundLayerId && (
                <StatusBadge status={"BOUND"} variant={"icon"} />
              )}
            </TableCell>
          </TableRow>
        ));
      })}
      {loading && <LoadingRow totalCols={totalCols} />}
      {showNoMatchesFound && (
        <BlankSlateRow
          message={`Sorry, no matches for '${searchText}'`}
          totalCols={totalCols}
        />
      )}
      {showBlankSlate && (
        <BlankSlateRow
          message={
            "You haven't posted any data yet, click the New Submission button to get started"
          }
          color={"silver"}
          totalCols={totalCols}
        />
      )}
    </TableBody>
  );
};

const SortMenu = ({
  anchorEl,
  closeMenu,
  sortColumn,
  changeSortColumn,
  sortOrder,
  changeSortOrder,
  resetSort,
}) => {
  const sortColumns = [
    { key: "createTime", label: "Created" },
    { key: "status", label: "Status" },
    { key: "applicant", label: "Insured" },
    { key: "service", label: "Service" },
    { key: "inception", label: "Inception" },
    { key: "premium", label: "Price" },
    { key: "limit", label: "Limit" },
    { key: "aggregate", label: "Agg. Limit" },
    { key: "deductible", label: "Deductible" },
  ];

  const getSortOrderLabels = (columnKey) => {
    const textColumns = ["status", "applicant", "service"];
    const dateColumns = ["createTime", "inception"];

    if (textColumns.includes(columnKey)) {
      return [
        { key: "asc", label: "A to Z" },
        { key: "desc", label: "Z to A" },
      ];
    } else if (dateColumns.includes(columnKey)) {
      return [
        { key: "desc", label: "Newest First" },
        { key: "asc", label: "Oldest First" },
      ];
    } else {
      return [
        { key: "desc", label: "High to Low" },
        { key: "asc", label: "Low to High" },
      ];
    }
  };

  const sortOrders = getSortOrderLabels(sortColumn);
  const isDefaultSort = sortColumn === "createTime" && sortOrder === "desc";

  return (
    <Menu
      anchorEl={anchorEl}
      open={anchorEl != null}
      onClose={(_, reason) => {
        if (reason === "backdropClick") closeMenu();
      }}
      slotProps={{
        paper: {
          sx: { minWidth: "14rem" },
        },
      }}
    >
      <MenuItem disabled>
        <Typography variant={"caption"}>{"Sort by"}</Typography>
      </MenuItem>

      {sortColumns.map((column) => (
        <MenuItem onClick={() => changeSortColumn(column.key)} key={column.key}>
          {column.key === sortColumn && (
            <ListItemIcon>
              <SelectedIcon fontSize={"small"} />
            </ListItemIcon>
          )}
          <ListItemText inset={column.key !== sortColumn}>
            {column.label}
          </ListItemText>
        </MenuItem>
      ))}

      <Divider />

      <MenuItem disabled>
        <Typography variant={"caption"}>{"Order"}</Typography>
      </MenuItem>

      {sortOrders.map((order) => (
        <MenuItem onClick={() => changeSortOrder(order.key)} key={order.key}>
          {order.key === sortOrder && (
            <ListItemIcon>
              <SelectedIcon fontSize={"small"} />
            </ListItemIcon>
          )}
          <ListItemText inset={order.key !== sortOrder}>
            {order.label}
          </ListItemText>
        </MenuItem>
      ))}

      {!isDefaultSort && (
        <>
          <Divider />
          <MenuItem onClick={resetSort}>
            <ListItemText>{"Clear Sort"}</ListItemText>
          </MenuItem>
        </>
      )}
    </Menu>
  );
};

const List = () => {
  const theme = useTheme();
  const isFormsAdmin = useSelector(userSelectors.isFormsAdminUser);
  const [searchText, setSearchText] = useState(null);
  const [sortColumn, setSortColumn] = useState("createTime");
  const [sortOrder, setSortOrder] = useState("desc");
  const [sortAnchorEl, setSortAnchorEl] = useState(null);

  const { data, isLoading } = bffService.useListFormsQuery(null, {
    refetchOnMountOrArgChange: true,
  });

  const openSortMenu = (e) => setSortAnchorEl(e.currentTarget);
  const closeSortMenu = () => setSortAnchorEl(null);

  const getDefaultSortOrder = (columnKey) => {
    const textColumns = ["status", "applicant", "service"];
    return textColumns.includes(columnKey) ? "asc" : "desc";
  };

  const changeSortColumn = (columnKey) => {
    setSortColumn(columnKey);
    setSortOrder(getDefaultSortOrder(columnKey));
  };

  const resetSort = () => {
    setSortColumn("createTime");
    setSortOrder("desc");
    closeSortMenu();
  };

  const isDefaultSort = sortColumn === "createTime" && sortOrder === "desc";

  const getSortValue = (item, column) => {
    switch (column) {
      case "createTime":
        return new Date(item.createTime || 0);
      case "status":
        return item.status || "";
      case "applicant":
        return item.formData.applicant || "";
      case "service":
        return item.formData.service || "";
      case "inception":
        return new Date(item.formData.inception || 0);
      case "premium":
        return item.quote?.premium || 0;
      case "limit":
        return item.formData.limit || 0;
      case "aggregate":
        return item.formData.aggregate || 0;
      case "deductible":
        return item.formData.deductible || 0;
      default:
        return "";
    }
  };

  const normalizeText = (text) => {
    if (!text || typeof text === "boolean") {
      return "";
    }
    return (text ?? "").toString().toLowerCase().trim().replaceAll("_", " ");
  };

  const normalizedSearchText = normalizeText(searchText);
  const filteredData = (data ?? []).filter((x) => {
    return (
      normalizedSearchText === "" ||
      normalizeText(x.status).includes(normalizedSearchText) ||
      (isFormsAdmin &&
        normalizeText(x.createdByUserEmail).includes(normalizedSearchText)) ||
      Object.values(x.formData).some((v) =>
        normalizeText(v).includes(normalizedSearchText)
      )
    );
  });

  const dataToDisplay = [...filteredData].sort((a, b) => {
    const aValue = getSortValue(a, sortColumn);
    const bValue = getSortValue(b, sortColumn);

    let comparison = 0;
    if (aValue < bValue) comparison = -1;
    if (aValue > bValue) comparison = 1;

    return sortOrder === "asc" ? comparison : -comparison;
  });

  const showBlankSlate = data?.length === 0;
  const showNoMatchesFound =
    normalizedSearchText !== "" &&
    data?.length > 0 &&
    dataToDisplay.length === 0;

  return (
    <Dashboard>
      <DashboardContent isFullWidth>
        <Component
          title={"Submissions"}
          options={
            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              sx={{ width: "100%", marginTop: "0.5rem" }}
            >
              <Stack direction={"row"} spacing={1}>
                <InlineSearch
                  placeholder={"Quick Search"}
                  onChange={(searchText) => setSearchText(searchText)}
                />
                <Tooltip title={"Sort"}>
                  <Box sx={{ position: "relative" }}>
                    <MuiIconButton
                      onClick={openSortMenu}
                      sx={{ color: "text.secondary" }}
                    >
                      <SortIcon fontSize={"small"} />
                      <Fade in={!isDefaultSort}>
                        <Box
                          sx={{
                            position: "absolute",
                            right: "4px",
                            top: "7px",
                            width: "8px",
                            height: "8px",
                            borderRadius: "4px",
                            border: "1px solid white",
                            background: theme.brand.accent[500],
                          }}
                        />
                      </Fade>
                    </MuiIconButton>
                  </Box>
                </Tooltip>
              </Stack>
              <Link to={"/forms/new"}>
                <Button variant={"contained"} disableElevation>
                  {"New Submission"}
                </Button>
              </Link>
            </Stack>
          }
        >
          <TableContainer sx={{ overflowX: "initial" }}>
            <Table stickyHeader>
              <TableHeadings includeUser={isFormsAdmin} />
              <TableContent
                data={dataToDisplay}
                loading={isLoading}
                includeUser={isFormsAdmin}
                searchText={searchText}
                showBlankSlate={showBlankSlate}
                showNoMatchesFound={showNoMatchesFound}
              />
            </Table>
          </TableContainer>
          <SortMenu
            anchorEl={sortAnchorEl}
            closeMenu={closeSortMenu}
            sortColumn={sortColumn}
            changeSortColumn={changeSortColumn}
            sortOrder={sortOrder}
            changeSortOrder={setSortOrder}
            resetSort={resetSort}
          />
        </Component>
      </DashboardContent>
    </Dashboard>
  );
};

export default List;
